import type { ClusterDto } from '@services/api/api.service';
import type { DealerGeoviewViewport } from './actions';

export interface DealerGeoviewClusterTile {
  key: string;
  zoomLevel: number;
  tileX: number;
  tileY: number;
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
}

const WEB_MERCATOR_TILE_SIZE = 256;
const CLUSTER_REQUEST_TILE_SIZE = 840;
const MAXIMUM_WEB_MERCATOR_LATITUDE = 85.05112878;
export const CLUSTER_TILE_TTL_MS = 30_000;

export function isClusterTileFresh(status: string, loadedAt: number | null, now = Date.now()): boolean {
  return status === 'loaded' && loadedAt != null && now - loadedAt < CLUSTER_TILE_TTL_MS;
}

export function mergeGeoviewClusters(clusters: ClusterDto[]): ClusterDto[] {
  const clustersById = new Map<string, ClusterDto>();

  for (const cluster of clusters) {
    if (!cluster.id) {
      continue;
    }

    const existing = clustersById.get(cluster.id);
    if (!existing) {
      clustersById.set(cluster.id, { ...cluster });
      continue;
    }

    const existingCount = existing.vehicleCount || 0;
    const incomingCount = cluster.vehicleCount || 0;
    const vehicleCount = existingCount + incomingCount;
    const existingWeight = existingCount || 1;
    const incomingWeight = incomingCount || 1;
    const totalWeight = existingWeight + incomingWeight;

    clustersById.set(cluster.id, {
      ...existing,
      latitude: ((existing.latitude || 0) * existingWeight + (cluster.latitude || 0) * incomingWeight) / totalWeight,
      longitude: ((existing.longitude || 0) * existingWeight + (cluster.longitude || 0) * incomingWeight) / totalWeight,
      vehicleCount,
      activeVehicleCount: (existing.activeVehicleCount || 0) + (cluster.activeVehicleCount || 0),
      alarmVehicleCount: (existing.alarmVehicleCount || 0) + (cluster.alarmVehicleCount || 0),
      dueMaintenanceVehicleCount: (existing.dueMaintenanceVehicleCount || 0) + (cluster.dueMaintenanceVehicleCount || 0),
      minLatitude: Math.min(existing.minLatitude ?? Infinity, cluster.minLatitude ?? Infinity),
      maxLatitude: Math.max(existing.maxLatitude ?? -Infinity, cluster.maxLatitude ?? -Infinity),
      minLongitude: Math.min(existing.minLongitude ?? Infinity, cluster.minLongitude ?? Infinity),
      maxLongitude: Math.max(existing.maxLongitude ?? -Infinity, cluster.maxLongitude ?? -Infinity),
      vin: vehicleCount === 1 ? existing.vin || cluster.vin : null,
      name: vehicleCount === 1 ? existing.name || cluster.name : null,
    });
  }

  return Array.from(clustersById.values());
}

export function getVisibleClusterTiles(viewport: DealerGeoviewViewport): DealerGeoviewClusterTile[] {
  const zoomLevel = Math.max(0, Math.min(20, Math.floor(viewport.zoomLevel)));
  const worldSize = WEB_MERCATOR_TILE_SIZE * Math.pow(2, zoomLevel);
  const tileCount = Math.ceil(worldSize / CLUSTER_REQUEST_TILE_SIZE);
  const minimumY = latitudeToPixelY(viewport.maxLatitude, worldSize);
  const maximumY = latitudeToPixelY(viewport.minLatitude, worldSize);
  const firstTileY = getTileIndex(minimumY, tileCount, worldSize);
  const lastTileY = getTileIndex(maximumY - 0.000001, tileCount, worldSize);
  const longitudeRanges = viewport.minLongitude <= viewport.maxLongitude
    ? [[viewport.minLongitude, viewport.maxLongitude]]
    : [[viewport.minLongitude, 180], [-180, viewport.maxLongitude]];
  const tiles = new Map<string, DealerGeoviewClusterTile>();

  for (const [minimumLongitude, maximumLongitude] of longitudeRanges) {
    const minimumX = longitudeToPixelX(minimumLongitude, worldSize);
    const maximumX = maximumLongitude === 180
      ? worldSize
      : longitudeToPixelX(maximumLongitude, worldSize);
    const firstTileX = getTileIndex(minimumX, tileCount, worldSize);
    const lastTileX = getTileIndex(maximumX - 0.000001, tileCount, worldSize);

    for (let tileY = firstTileY; tileY <= lastTileY; tileY++) {
      for (let tileX = firstTileX; tileX <= lastTileX; tileX++) {
        const tile = createClusterTile(zoomLevel, tileX, tileY, worldSize);
        tiles.set(tile.key, tile);
      }
    }
  }

  return Array.from(tiles.values());
}

function createClusterTile(
  zoomLevel: number,
  tileX: number,
  tileY: number,
  worldSize: number,
): DealerGeoviewClusterTile {
  const minimumX = tileX * CLUSTER_REQUEST_TILE_SIZE;
  const maximumX = Math.min(minimumX + CLUSTER_REQUEST_TILE_SIZE, worldSize);
  const minimumY = tileY * CLUSTER_REQUEST_TILE_SIZE;
  const maximumY = Math.min(minimumY + CLUSTER_REQUEST_TILE_SIZE, worldSize);

  return {
    key: `${zoomLevel}:${tileX}:${tileY}`,
    zoomLevel,
    tileX,
    tileY,
    minLatitude: pixelYToLatitude(maximumY, worldSize),
    maxLatitude: pixelYToLatitude(minimumY, worldSize),
    minLongitude: pixelXToLongitude(minimumX, worldSize),
    maxLongitude: pixelXToLongitude(maximumX, worldSize),
  };
}

function getTileIndex(pixel: number, tileCount: number, worldSize: number): number {
  const pixelInsideWorld = Math.max(0, Math.min(pixel, worldSize - 0.000001));
  return Math.max(0, Math.min(tileCount - 1, Math.floor(pixelInsideWorld / CLUSTER_REQUEST_TILE_SIZE)));
}

function longitudeToPixelX(longitude: number, worldSize: number): number {
  return (longitude + 180) / 360 * worldSize;
}

function latitudeToPixelY(latitude: number, worldSize: number): number {
  const clampedLatitude = Math.max(
    -MAXIMUM_WEB_MERCATOR_LATITUDE,
    Math.min(MAXIMUM_WEB_MERCATOR_LATITUDE, latitude),
  );
  const latitudeRadians = clampedLatitude * Math.PI / 180;
  const sinLatitude = Math.sin(latitudeRadians);
  return (0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) * worldSize;
}

function pixelXToLongitude(pixelX: number, worldSize: number): number {
  return pixelX / worldSize * 360 - 180;
}

function pixelYToLatitude(pixelY: number, worldSize: number): number {
  const mercatorY = Math.PI * (1 - 2 * pixelY / worldSize);
  return Math.atan(Math.sinh(mercatorY)) * 180 / Math.PI;
}
